import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CalendarIcon,
  DownloadIcon,
  FilterIcon,
  XIcon,
} from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";

import { Button, Card, CardContent } from "@/components";
import { Calendar } from "@/components/ui/calendar";
import { Combobox } from "@/components/ui/combobox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useI18n } from "@/context";
import { cn } from "@/lib/utils";

import { TSupplierReportsFiltersFormData } from "../../supplier-reports.schema";

interface ISupplierReportsFiltersProps {
  form: UseFormReturn<TSupplierReportsFiltersFormData>;
  onApplyFilters: (data: TSupplierReportsFiltersFormData) => void;
  onClearFilters: () => void;
  onExportCsv: () => void;
  isExporting: boolean;
  statusOptions: Array<{ value: string; label: string }>;
  supplierOptions: Array<{ value: string; label: string }>;
  productOptions: Array<{ value: string; label: string }>;
  selectedSupplierLabel: string | null;
}

export const SupplierReportsFilters: React.FC<ISupplierReportsFiltersProps> = ({
  form,
  onApplyFilters,
  onClearFilters,
  onExportCsv,
  isExporting,
  statusOptions,
  supplierOptions,
  productOptions,
  selectedSupplierLabel,
}) => {
  const { t } = useI18n();
  const selectedSupplierId = form.watch("insurance_company_id");

  return (
    <Card className="p-4 mb-6">
      <CardContent>
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FilterIcon className="h-4 w-4" />
            {t("reports.supplierTab.filtersTitle")}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onExportCsv}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            <DownloadIcon className="h-4 w-4" />
            {isExporting
              ? t("generic.loading")
              : t("reports.export.button")}
          </Button>
        </div>

        {selectedSupplierLabel && (
          <p className="text-sm text-muted-foreground mb-4">
            {t("reports.supplierTab.viewingSupplier")}:{" "}
            <span className="font-medium text-foreground">
              {selectedSupplierLabel}
            </span>
          </p>
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onApplyFilters)}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="insurance_company_id"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        label={t("reports.supplierTab.supplier")}
                        placeholder={t(
                          "reports.supplierTab.supplierPlaceholder"
                        )}
                        options={supplierOptions}
                        value={field.value || undefined}
                        onChange={(value) => {
                          field.onChange(value ?? "");
                          form.setValue("product_id", "");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="product_id"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        label={t("reports.filters.product")}
                        placeholder={t("reports.filters.productPlaceholder")}
                        options={productOptions}
                        value={field.value || undefined}
                        onChange={(value) => field.onChange(value ?? "")}
                        disabled={
                          !selectedSupplierId || selectedSupplierId === ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        label={t("reports.filters.status")}
                        placeholder={t("reports.filters.statusPlaceholder")}
                        options={statusOptions}
                        value={field.value || undefined}
                        onChange={(value) => field.onChange(value ?? "")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value
                                ? field.value
                                : t("reports.filters.startDate")}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              locale={ptBR}
                              selected={
                                field.value
                                  ? parse(field.value, "dd/MM/yyyy", new Date())
                                  : undefined
                              }
                              onSelect={(date) => {
                                field.onChange(
                                  date ? format(date, "dd/MM/yyyy") : ""
                                );
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value
                                ? field.value
                                : t("reports.filters.endDate")}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              locale={ptBR}
                              selected={
                                field.value
                                  ? parse(field.value, "dd/MM/yyyy", new Date())
                                  : undefined
                              }
                              onSelect={(date) => {
                                field.onChange(
                                  date ? format(date, "dd/MM/yyyy") : ""
                                );
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={onClearFilters}
                className="flex items-center gap-2"
              >
                <XIcon className="h-4 w-4" />
                {t("reports.filters.clear")}
              </Button>
              <Button type="submit" className="flex items-center gap-2">
                {t("reports.supplierTab.apply")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
